import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiltersToolbarFilter } from '@hqo/filters-toolbar';
import { MultiSelect, MultiSelectOption } from '@hqo/multi-select';
import { Badge } from '@hqo/ui-atoms';
import { useIntl } from 'react-intl';

import { setStatusFilter } from 'store/visits/actions';
import { getStatusFilter } from 'store/visits/selectors';
import { VisitStatus } from 'store/visits/types';
import { useBuildingData } from 'store/building/hooks';
import { cn } from '@hqo/react-hooks';

const DEFAULT_STATUS_OPTIONS: VisitStatus[] = [VisitStatus.INVITED, VisitStatus.CANCELED];

export const StatusFilter = (): JSX.Element => {
  const dispatch = useDispatch();
  const value = useSelector(getStatusFilter);
  const { supportedStatuses } = useBuildingData();
  const { formatMessage } = useIntl();
  const statusLabel = formatMessage({ id: 'labels.status.label', defaultMessage: 'Status' });
  const selectAllLabel = formatMessage({ id: 'labels.filtersToolbar.selectAll', defaultMessage: 'Select all' });

  const statusOptions = useMemo(
    () =>
      supportedStatuses
        ? (supportedStatuses.filter((s) => Object.values(VisitStatus).includes(s as VisitStatus)) as VisitStatus[])
        : DEFAULT_STATUS_OPTIONS,
    [supportedStatuses],
  );

  const options: MultiSelectOption[] = useMemo(
    () =>
      statusOptions.map((status) => ({
        label: formatMessage({ id: `labels.status.type.${status}`, defaultMessage: status }).replace(/^./, (char) =>
          char.toUpperCase(),
        ),
        value: status,
      })),
    [formatMessage, statusOptions],
  );

  const handleChange = useCallback(
    (selected: MultiSelectOption[]) => {
      dispatch(setStatusFilter(selected.map(({ value: filterValue }) => filterValue as VisitStatus)));
    },
    [dispatch],
  );

  const selectedOptions = useMemo(
    () => options.filter((option) => value.includes(option.value as VisitStatus)),
    [options, value],
  );

  const moreLabel = useMemo(
    () =>
      value.length ? (
        <span className="inline-flex items-center gap-1">
          {statusLabel}
          <Badge className="px-1 py-0 text-[11px] leading-none">{value.length}</Badge>
        </span>
      ) : undefined,
    [statusLabel, value.length],
  );

  return (
    <FiltersToolbarFilter id="status" priority={10} isSet={selectedOptions.length > 0} className="min-w-52 max-w-70">
      <MultiSelect
        className={cn(
          'bg-shared-white min-h-8 h-8 rounded-lg text-sm font-normal [&>div>span]:font-normal [&>div>span]:text-sm hover:bg-shared-white',
        )}
        canClear={false}
        canClose={false}
        placeholder={statusLabel}
        selectedOptions={selectedOptions}
        options={options}
        onChange={handleChange}
        maxCount={selectedOptions.length > 1 ? 0 : 1}
        hideMoreDeselect
        moreLabel={moreLabel}
        selectAllLabel={selectAllLabel}
      />
    </FiltersToolbarFilter>
  );
};
